import { motion } from "framer-motion";
import { PlayCircle, Clock } from "lucide-react";
import { COURSES, WORKFLOW_HEADING } from "../constants/content";

const cardVariant = {
  hidden: { opacity: 0, y: 60 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

const THEME = {
  pink: {
    gradient: "bg-gradient-to-br from-[#D88C9A] to-[#c26f80]",
    badgeBg: "bg-white/20",
    ring: "ring-[#D88C9A]/30",
    text: "text-[#D88C9A]",
  },
  blue: {
    gradient: "bg-gradient-to-br from-[#8FA5C7] to-[#6f88ac]",
    badgeBg: "bg-white/20",
    ring: "ring-[#8FA5C7]/30",
    text: "text-[#8FA5C7]",
  },
};

export const CourseShowcase = () => {
  return (
    <section id="courses" data-testid="course-showcase-section" className="bg-[#F5F5F5] pt-4 md:pt-6 pb-10 md:pb-14">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12 md:mb-14">
          <span data-testid="workflow-eyebrow" className="text-xs uppercase tracking-[0.2em] font-medium text-gold">
            {WORKFLOW_HEADING.eyebrow}
          </span>
          <h2
            data-testid="workflow-heading"
            className="font-serif font-medium text-4xl md:text-5xl tracking-tight leading-[1.1] text-ink mt-4 max-w-2xl mx-auto"
          >
            {WORKFLOW_HEADING.heading}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {COURSES.map((course, i) => {
            const theme = THEME[course.theme];
            return (
              <motion.article
                key={course.id}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={cardVariant}
                data-testid={`course-card-${course.id}`}
                className={`rounded-2xl overflow-hidden flex flex-col h-full shadow-[0_8px_30px_rgb(0,0,0,0.06)] ring-1 ${theme.ring} hover:shadow-[0_20px_40px_rgb(0,0,0,0.1)] transition-shadow duration-300 ${i === 0 ? "lg:order-1" : "lg:order-3"}`}
              >
                <div className={`${theme.gradient} p-8 md:p-10 text-white`}>
                  <span className={`inline-flex items-center rounded-full ${theme.badgeBg} px-4 py-1 text-xs uppercase tracking-wide font-medium`}>
                    {course.badge}
                  </span>
                  <h3 className="font-serif text-3xl md:text-4xl leading-[1.15] mt-4">
                    {course.name}
                  </h3>
                  <p className="text-sm uppercase tracking-[0.12em] text-white/70 mt-1">
                    {course.fullName}
                  </p>

                  <div className="flex items-center gap-6 mt-6 pt-6 border-t border-white/20">
                    <div data-testid={`course-${course.id}-videos`} className="flex items-center gap-2 text-sm">
                      <PlayCircle className="h-4 w-4" />
                      <span>{course.videos}</span>
                    </div>
                    <div data-testid={`course-${course.id}-hours`} className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4" />
                      <span>{course.hours}</span>
                    </div>
                  </div>
                </div>

                <div className="p-8 flex flex-col bg-white">
                  <p className={`font-serif italic text-lg ${theme.text}`}>{course.tagline}</p>
                  <p className="text-sm font-medium text-slateDeep mt-4">{course.highlight}</p>
                </div>
              </motion.article>
            );
          })}

          <motion.div
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={cardVariant}
            data-testid="course-card-bundle-included"
            className="rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)] ring-1 ring-black/5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.1)] transition-shadow duration-300 flex items-center justify-center bg-[#FBF4F1] h-full lg:order-2"
          >
            <img
              src="/images/bundle_included.webp"
              alt="Included in Bundle: special e-book for both courses, instant access on purchase, certificate on completion, same content as offline batch"
              data-testid="bundle-included-image"
              className="w-full h-full object-contain"
              width="900"
              height="900"
              loading="lazy"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
